// ts-rest contract for the Relationship module (three-tier client model
// Stage 4, ADR-0025 D4/D7).
//
// One area covering the RelationshipsPanel reads (both directions merged,
// from the perspective of ?partyId=), the desk/ops relationship mutations,
// the roles list feeding every role dropdown, and the admin-only catalogue
// management endpoints.
//
// DELIBERATE CLOSED-ENUM EXCEPTION (PRD §12 / ADR-0025 D4): `role` fields
// cross the wire as `z.string()`, never `z.enum`. Roles are a runtime-
// addable data catalogue — an admin adds "IFA" from the Administration page
// and uses it immediately, with no redeploy — so the wire cannot pin a
// closed vocabulary. The cost (no compile-time exhaustiveness on the one
// field where a typo has behavioural consequence) is bought back at the
// write boundary: unknown/inactive keys are refused (UNKNOWN_ROLE), every
// behavioural rule reads a catalogue flag, and the no-hardcoded-role guard
// forbids key literals outside the server's role-catalogue module.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodyWithCode as errorBody } from '../schemas/common';

const c = initContract();

// ---------------------------------------------------------------------------
// Enums (closed domain values — snake_case VALUES, camelCase keys)
// ---------------------------------------------------------------------------

/**
 * Party types are Object Type KEYS — the kebab-case identifiers of the
 * three tiers, pinned verbatim (structural registry identifiers, not a new
 * enum vocabulary; the snake_case-values convention governs business
 * vocabularies like `appliesTo` below).
 */
export const relationshipPartyTypeSchema = z.enum([
    'contact',
    'suitability-record',
    'tercero-client',
]);
export type RelationshipPartyType = z.infer<typeof relationshipPartyTypeSchema>;

export const relationshipAppliesToSchema = z.enum([
    'contact_suitability',
    'contact_client',
    'contact_contact',
    'suitability_client',
]);
export type RelationshipAppliesTo = z.infer<typeof relationshipAppliesToSchema>;

export const relationshipAgeBandSchema = z.enum(['minor', 'adult']);
export type RelationshipAgeBand = z.infer<typeof relationshipAgeBandSchema>;

/**
 * The relationship kind of an unordered party-type pair, or null when the
 * pair is unsupported. Mirrors the ontology's canonical-order helper for
 * UI-side role filtering (the web layer cannot import the ontology runtime
 * — contract-boundary guard).
 */
export function relationshipPairKind(
    a: RelationshipPartyType,
    b: RelationshipPartyType,
): RelationshipAppliesTo | null {
    const set = [a, b].sort().join('+');
    switch (set) {
        case 'contact+suitability-record':
            return 'contact_suitability';
        case 'contact+tercero-client':
            return 'contact_client';
        case 'contact+contact':
            return 'contact_contact';
        case 'suitability-record+tercero-client':
            return 'suitability_client';
        default:
            return null;
    }
}

/** Tier labels, in one place so the per-record panel and the book-level
 *  directory can never disagree about what a party type is called. */
export const RELATIONSHIP_PARTY_TYPE_LABELS: Record<RelationshipPartyType, string> = {
    contact: 'Contact',
    'suitability-record': 'Suitability record',
    'tercero-client': 'Client',
};

/**
 * SINGLE-ROLE KINDS. For `contact_suitability` and `contact_client` the stored
 * role describes the CONTACT's role on the record / client; the other end
 * mirrors the same key by storage convention and must never be rendered (a
 * suitability record must not describe itself as "Sole Owner" — see
 * `specs/wealth/client/relationship.md`).
 *
 * This is a comparison of relationship KINDS, not of role keys — the
 * no-hardcoded-role guard is about the latter.
 */
export function isSingleRoleRelationshipKind(kind: RelationshipAppliesTo): boolean {
    return kind === 'contact_suitability' || kind === 'contact_client';
}

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

export const relationshipRoleSchema = z.object({
    roleId: z.string(),
    /** Catalogue key — a plain string BY DESIGN (see header comment). */
    key: z.string(),
    label: z.string(),
    appliesTo: relationshipAppliesToSchema,
    displayPriority: z.union([z.literal(1), z.literal(2)]).nullable(),
    nominatedRepresentative: z.boolean(),
    ageBand: relationshipAgeBandSchema.nullable(),
    pairKey: z.string().nullable(),
    sortOrder: z.number().int(),
    active: z.boolean(),
});
export type RelationshipRoleDto = z.infer<typeof relationshipRoleSchema>;

export const listRelationshipRolesResponseSchema = z.object({
    roles: z.array(relationshipRoleSchema),
});
export type ListRelationshipRolesResponse = z.infer<typeof listRelationshipRolesResponseSchema>;

// ---------------------------------------------------------------------------
// Panel rows — both directions merged, from ?partyId='s perspective
// ---------------------------------------------------------------------------

export const relationshipRowSchema = z.object({
    relationshipId: z.string(),
    kind: relationshipAppliesToSchema,
    /** The queried party's own end. */
    ownRole: z.object({ key: z.string(), label: z.string() }),
    /** The other end — one click to any of them (PRD §10). */
    counterpartType: relationshipPartyTypeSchema,
    counterpartId: z.string(),
    /** Display name resolved server-side (contact name / SUR reference /
     * client reference); null when the row is outside the caller's read. */
    counterpartName: z.string().nullable(),
    /** Counterpart-end role. The label is DERIVED server-side for
     * age-banded roles (Child Minor/Adult) from the contact's date of
     * birth against clockNow() — the stored key is not authoritative. */
    counterpartRole: z.object({ key: z.string(), label: z.string() }),
    startDate: z.string().nullable(),
    /** Ended links stay visible (greyed, with their end date) — never
     * hidden, never deleted. */
    endDate: z.string().nullable(),
    createdAt: z.string(),
    note: z.string().nullable(),
});
export type RelationshipRow = z.infer<typeof relationshipRowSchema>;

export const listRelationshipsResponseSchema = z.object({
    rows: z.array(relationshipRowSchema),
});
export type ListRelationshipsResponse = z.infer<typeof listRelationshipsResponseSchema>;

// ---------------------------------------------------------------------------
// Book-level rows (SEP-176) — the whole book at once, in STORED order
// ---------------------------------------------------------------------------

/**
 * One end of a relationship, as the book-level directory renders it.
 *
 * Unlike {@link relationshipRowSchema}, which is written from ONE party's
 * perspective (`ownRole` / `counterpart*`), this row has no perspective: there
 * is no "current record" on a book-level list. Party 1 and Party 2 are the
 * STORED canonical order (contact → suitability-record, contact →
 * tercero-client, suitability-record → tercero-client, contact ↔ contact
 * sorted) — which is precisely what makes a party-type filter on each side
 * meaningful, and what the Titan Party 1 / Role 1 / Role 2 / Party 2 layout
 * has always shown.
 */
export const relationshipPartySchema = z.object({
    partyType: relationshipPartyTypeSchema,
    partyId: z.string(),
    /**
     * The party's display label, resolved server-side within the CALLER's read
     * scope (ADR-0024) and null when the row is outside it — the id stays so
     * the row still renders, the name is withheld. One format per tier:
     *
     *   - contact — the full display name, e.g. `Lucinda Pryce`;
     *   - suitability record — `SUR-1001 · Pryce`: the reference, then the
     *     same human anchor the SEP-180 picker label uses (primary contact's
     *     surname, else their full name, else the entity name), degrading to a
     *     bare `SUR-1001` when nothing resolves;
     *   - client — the account umbrella's parent code, falling back to its
     *     raw client reference.
     */
    label: z.string().nullable(),
    /**
     * The role at THIS end, or null when the tier convention hides it — see
     * {@link isSingleRoleRelationshipKind}. Resolved against the
     * `relationship_roles` catalogue (never a hard-coded label), with the
     * age-banded Child (Minor)/(Adult) label derived on read (ADR-0025 D7).
     */
    role: z.object({ key: z.string(), label: z.string() }).nullable(),
});
export type RelationshipParty = z.infer<typeof relationshipPartySchema>;

export const bookRelationshipRowSchema = z.object({
    relationshipId: z.string(),
    kind: relationshipAppliesToSchema,
    party1: relationshipPartySchema,
    party2: relationshipPartySchema,
    startDate: z.string().nullable(),
    /** Ended links stay on the wire and stay on the page — greyed, with their
     * end date. Nothing is ever hard deleted (ADR-0025 D7). */
    endDate: z.string().nullable(),
    createdAt: z.string(),
    note: z.string().nullable(),
});
export type BookRelationshipRow = z.infer<typeof bookRelationshipRowSchema>;

/** Counts over the caller's WHOLE readable set — the page's party-type filters
 *  are applied in the browser, so the header cards do not move when one does. */
export const bookRelationshipsStatsSchema = z.object({
    total: z.number().int(),
    active: z.number().int(),
    ended: z.number().int(),
});
export type BookRelationshipsStats = z.infer<typeof bookRelationshipsStatsSchema>;

export const listBookRelationshipsResponseSchema = z.object({
    rows: z.array(bookRelationshipRowSchema),
    stats: bookRelationshipsStatsSchema,
});
export type ListBookRelationshipsResponse = z.infer<typeof listBookRelationshipsResponseSchema>;

// ---------------------------------------------------------------------------
// Mutation requests / responses
// ---------------------------------------------------------------------------

export const createRelationshipRequestSchema = z.object({
    party1Type: relationshipPartyTypeSchema,
    party1Id: z.string().min(1),
    /** Role key for party 1's end — plain string by design (header note). */
    party1Role: z.string().min(1),
    party2Type: relationshipPartyTypeSchema,
    party2Id: z.string().min(1),
    party2Role: z.string().min(1),
    /** Calendar date — matches the create-relationship Action input, so a
     * malformed date refuses at the contract with a proper message. */
    startDate: z.string().date().optional(),
    note: z.string().optional(),
});
export type CreateRelationshipRequest = z.infer<typeof createRelationshipRequestSchema>;

/** Create/update responses carry warn-never-block notices (PRD §9.3 —
 * a second Nominated Representative warns; the UI renders them amber). */
export const relationshipMutationResponseSchema = z.object({
    ok: z.boolean(),
    relationshipId: z.string(),
    warnings: z.array(z.string()),
});
export type RelationshipMutationResponse = z.infer<typeof relationshipMutationResponseSchema>;

export const updateRelationshipRolesRequestSchema = z.object({
    party1Role: z.string().min(1),
    party2Role: z.string().min(1),
});
export type UpdateRelationshipRolesRequest = z.infer<typeof updateRelationshipRolesRequestSchema>;

// ---------------------------------------------------------------------------
// Admin role-catalogue requests
// ---------------------------------------------------------------------------

export const createRelationshipRoleRequestSchema = z.object({
    key: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/),
    label: z.string().min(1),
    appliesTo: relationshipAppliesToSchema,
    displayPriority: z
        .union([z.literal(1), z.literal(2)])
        .nullable()
        .optional(),
    nominatedRepresentative: z.boolean().optional(),
    ageBand: relationshipAgeBandSchema.nullable().optional(),
    pairKey: z.string().nullable().optional(),
    sortOrder: z.number().int().optional(),
});
export type CreateRelationshipRoleRequest = z.infer<typeof createRelationshipRoleRequestSchema>;

export const updateRelationshipRoleRequestSchema = z.object({
    label: z.string().min(1).optional(),
    sortOrder: z.number().int().optional(),
    /** Reactivation only — deactivation has its own guarded endpoint. */
    active: z.literal(true).optional(),
});
export type UpdateRelationshipRoleRequest = z.infer<typeof updateRelationshipRoleRequestSchema>;

const okRole = z.object({ ok: z.boolean(), roleId: z.string() });
const okRelationship = z.object({ ok: z.boolean(), relationshipId: z.string() });

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const relationshipsContract = c.router(
    {
        listRoles: {
            method: 'GET',
            path: '/relationships/roles',
            responses: { 200: listRelationshipRolesResponseSchema },
            summary:
                'The role catalogue — feeds every role dropdown (active) and the admin card (all)',
        },
        listBookRelationships: {
            method: 'GET',
            path: '/relationships/book',
            // NO QUERY. The caller's whole readable set comes back unpaged, so
            // a server-side party-type filter would buy nothing and cost the
            // page its stability: the filters are react-query key material, so
            // every change would mint an uncached key and blank the header
            // cards for a round trip. The page filters in the browser.
            responses: { 200: listBookRelationshipsResponseSchema },
            summary:
                "Every relationship in the caller's book, Party 1 / Role 1 / Role 2 / Party 2 (SEP-176)",
        },
        listRelationships: {
            method: 'GET',
            path: '/relationships',
            query: z.object({ partyId: z.string().min(1) }),
            responses: { 200: listRelationshipsResponseSchema },
            summary: 'Every relationship a party carries, both directions merged (PRD §10)',
        },
        createRelationship: {
            method: 'POST',
            path: '/relationships',
            body: createRelationshipRequestSchema,
            responses: { 200: relationshipMutationResponseSchema, 400: errorBody, 404: errorBody },
            summary:
                'Create a relationship (create-relationship Action — same-owner, canonical order, ownership uniqueness)',
        },
        updateRelationshipRoles: {
            method: 'PATCH',
            path: '/relationships/:relationshipId/roles',
            body: updateRelationshipRolesRequestSchema,
            responses: { 200: relationshipMutationResponseSchema, 400: errorBody, 404: errorBody },
            summary: 'Re-role both ends of a relationship (update-relationship-roles Action)',
        },
        endRelationship: {
            method: 'POST',
            path: '/relationships/:relationshipId/end',
            body: z.object({}).optional(),
            responses: { 200: okRelationship, 400: errorBody, 404: errorBody },
            summary: 'End a relationship — sets endDate, never deletes (end-relationship Action)',
        },
        createRole: {
            method: 'POST',
            path: '/relationship-roles',
            body: createRelationshipRoleRequestSchema,
            responses: { 200: okRole, 400: errorBody },
            summary: 'Admin: add a role to the catalogue (create-relationship-role Action)',
        },
        updateRole: {
            method: 'PATCH',
            path: '/relationship-roles/:roleId',
            body: updateRelationshipRoleRequestSchema,
            responses: { 200: okRole, 400: errorBody, 404: errorBody },
            summary:
                'Admin: edit a role label / sort order, or reactivate (update-relationship-role Action)',
        },
        deactivateRole: {
            method: 'POST',
            path: '/relationship-roles/:roleId/deactivate',
            body: z.object({}).optional(),
            responses: { 200: okRole, 400: errorBody, 404: errorBody },
            summary:
                'Admin: deactivate a role — refused while a display-priority role is in use (deactivate-relationship-role Action)',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
